import React from "react";
import { Col } from "react-bootstrap";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import { ElectricalData } from "./ProjectDetails.types.tsx";
import PerformanceDataCard from "./cards/PerformanceDataCard.tsx";
import HeatExchangerDataCard from "./cards/HeatExchangerDataCard.tsx";
import CondenserDataCard from "./cards/CondenserDataCard.tsx";
import ElectricalDataCard from "./cards/ElectricalDataCard.tsx";

import {
  DownloadLink,
  HeaderLabel,
  ProjectDetailsLabel,
  ProjectDetailsLabelBold,
  ProjectDetailsTitle,
  SubHeaderLabel,
} from "../Labels.tsx";

import { useHomeContext } from "../../views/Home/HomeView.context.tsx";

const ProjectDetailsUI: React.FC = () => {
  const { t } = useTranslation();
  const {
    handleDownloadPDF,
    showProjectDetailsOptions,
    isLoading,
    loadingComponent,
    projectDetailsData,
    unitSystem,
  } = useHomeContext();

  if (isLoading) return loadingComponent;

  const {
    created_at,
    performance_data: {
      performance_data,
      heat_exchanger_data,
      condenser_data,
      electrical_data = [],
    } = {},
  } = projectDetailsData;

  const date = new Date(created_at);
  const formattedDate = `${date.toLocaleString("en-US", {
    month: "short",
  })}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;

  return (
    <Col xs={12} md={7} className="p-0">
      <div
        className="m-4 p-4 rounded-4 d-flex flex-column no-scrollbar projectDetails"
        style={{
          height: "calc(100vh - 3rem)",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <HeaderLabel textAlign="left" margin="0">
            {t("projectDetails.header")}
          </HeaderLabel>

          <ThreeDotsVertical className="pointer-text" onClick={showProjectDetailsOptions} />
        </div>

        {/* Download */}
        <div className="d-flex justify-content-end mb-3">
          <SubHeaderLabel>
            {t("projectDetails.downloadLabel")}:{" "}
            <DownloadLink onClick={handleDownloadPDF}>{projectDetailsData?.name}.PDF</DownloadLink>
          </SubHeaderLabel>
        </div>

        {/* Title + date */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <ProjectDetailsTitle>{projectDetailsData?.name}</ProjectDetailsTitle>

          <div className="text-end">
            <ProjectDetailsLabelBold textAlign="right" margin="0">
              {t("projectDetails.creationDateLabel")}:
            </ProjectDetailsLabelBold>

            <ProjectDetailsLabel textAlign="right">{formattedDate}</ProjectDetailsLabel>
          </div>
        </div>

        {/* Description */}
        <div className="mb-3">
          <ProjectDetailsLabelBold>{t("projectDetails.descriptionLabel")}:</ProjectDetailsLabelBold>

          <ProjectDetailsLabel>{projectDetailsData?.description}</ProjectDetailsLabel>
        </div>

        {/* Performance */}
        <PerformanceDataCard performanceData={performance_data} />

        {/* Heat + Condenser */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-6">
            <HeatExchangerDataCard
              heatExchangerData={heat_exchanger_data}
              unitSystem={unitSystem}
            />
          </div>
          <div className="col-12 col-lg-6">
            <CondenserDataCard condenserData={condenser_data} />
          </div>
        </div>

        {/* Electrical */}
        <SubHeaderLabel textAlign="left" className="mb-2">
          {t("projectDetails.electricalDataTitle")}
        </SubHeaderLabel>

        <div className="row g-3">
          {electrical_data.map((item: ElectricalData, index: number) => (
            <div key={index} className="col-12 col-lg-6">
              <ElectricalDataCard electricalData={item} />
            </div>
          ))}
        </div>
      </div>
    </Col>
  );
};

export default ProjectDetailsUI;
